import { DrawerBody, DrawerFooter, Image } from '@chakra-ui/react'
import { mdiBookOpenVariantOutline } from '@mdi/js'
import Icon from '@mdi/react'
import { t } from 'i18next'
import { PrimitiveAtom, useAtom } from 'jotai'
import groupBy from 'lodash/groupBy'
import { useEffect, useState } from 'react'
import invariant from 'ts-invariant'

import DrawerDialog from '@/components/drawer'
import { Box } from '@/components/ui/box'
import { Button } from '@/components/ui/button'
import { Flex } from '@/components/ui/flex'
import { VStack } from '@/components/ui/stack'
import { Text } from '@/components/ui/typography'
import useColorModeValue from '@/hooks/useColorModeValue'
import robotIcon from '@/static/img/smart_toy.svg'
import robotIconDarkMode from '@/static/img/smart_toy_dark_mode.svg'
import { chatSettingsAtom, closePromptLibraryDrawer, getPrompts, newQuestionAtom, promptLibraryDrawerAtom } from '@/store/gigiChat/ChatAtom'
import { endPromptConversation } from '@/store/gigiChat/Visuals'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'
import { IGroupedPrompts, IPrompt } from '@/types/gigiChat/Prompts'
import { deepClone } from '@/utils'
import { getAndRemovePropertyFromUrlOnce } from '@/utils/chat.ts'

function validatePrompt(prompt: IPrompt) {
    if (!prompt.prompt) {
        console.warn(`Prompt has no "prompt"`, prompt)
    } else if (!prompt.title) {
        console.warn(`Prompt has no "title"`, prompt)
    } else if (!prompt.promptDescription) {
        console.warn(`Prompt has no "promptDescription"`, prompt)
    }
    return !!prompt.prompt && !!prompt.title && !!prompt.promptDescription
}

const PromptLibraryDrawer = () => {
    const borderColor = useColorModeValue('gray.300', 'gray.700')
    const [isOpen] = useAtom(promptLibraryDrawerAtom)
    const textColor = useColorModeValue('var(--chakra-colors-dark-700)', 'var(--chakra-colors-white)')
    const [, setQuestion] = useAtom<PrimitiveAtom<string | null>>(newQuestionAtom)
    const [chatSettings, setChatSettings] = useAtom<PrimitiveAtom<IConversationSettings | null>>(chatSettingsAtom)
    const [groupedPrompts, setGroupedPrompts] = useState<IGroupedPrompts>({})

    const onClose = () => {
        closePromptLibraryDrawer()
    }

    useEffect(() => {
        endPromptConversation()
        const getData = async () => {
            const prompts: IPrompt[] = await getPrompts()

            groupPrompts(prompts.filter(validatePrompt))

            const promptId = getAndRemovePropertyFromUrlOnce('promptId')
            if (promptId) {
                const prompt = prompts.find((p) => p.id === promptId)

                if (prompt) {
                    startPromptConversation(prompt)
                }
            }
        }

        void getData()
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [])

    const groupPrompts = (prompts: IPrompt[]) => {
        setGroupedPrompts(groupBy(prompts, 'type'))
    }

    const startPromptConversation = (prompt: IPrompt) => {
        invariant(chatSettings, 'chatSettings must not be null')
        const newSettings = deepClone(chatSettings)

        newSettings.processingStrategy = 'Writing'
        // in case of web search
        newSettings.processingStrategyWriting.model = 'gpt-4'

        // new props
        newSettings.processingModePromptLibrary = {
            metadata: `'{"promptId": ${prompt.id}}'`, // "{\"promptId\": 1}",
            label: prompt.title, //title
            systemPrompt: prompt?.systemPrompt || '',
            userPromptContext: prompt?.userContext || '',
        }

        // @Stephan prompt mode only if prompt has systemPrompt or userContext, otherwise it wil not be marked as prompt in chat UI
        if (prompt.systemPrompt || prompt.userContext) {
            newSettings.processingStrategyWriting.mode = 'promptLibrary'
        }

        setChatSettings(newSettings)
        setQuestion(prompt.prompt)
        onClose()
    }

    const assistantIcon = useColorModeValue(robotIcon, robotIconDarkMode)

    return (
        <DrawerDialog isOpen={isOpen} onClose={onClose} size="lg" noPadding={true}>
            <DrawerBody px="24px" pt={0} pb="16px">
                <Box>
                    <Flex
                        alignItems="center"
                        borderBottom={'1px solid'}
                        borderColor={borderColor}
                        paddingY="24px"
                        backgroundColor={useColorModeValue('white', 'black.800')}
                        position="sticky"
                        top="0"
                    >
                        <Icon
                            path={mdiBookOpenVariantOutline}
                            size="24px"
                            color={textColor}
                            style={{
                                marginRight: '18px',
                            }}
                        />
                        <Text
                            fontSize="21px"
                            fontFamily="GSKPrecision-Bold"
                            fontWeight="bold"
                            letterSpacing="0.25px"
                            display="flex"
                            lineHeight="20px"
                        >
                            {t('chat.promptLib.drawer.title')}
                        </Text>
                        <Text
                            fontSize="16px"
                            marginLeft="16px"
                            color="var(--chakra-colors-secondary-850)"
                            letterSpacing="-0.32px"
                            lineHeight="22px"
                        >
                            ({t('chat.promptLib.drawer.betaLabel')})
                        </Text>
                    </Flex>

                    <Box>
                        {Object.entries(groupedPrompts).map(([category, prompts]) => (
                            <div key={category}>
                                <Text color={'gray.500'} fontSize={'xs'} my="4">
                                    {category.toUpperCase()}
                                </Text>
                                <VStack alignItems={'flex-start'} gap="2">
                                    {prompts.map((prompt) => (
                                        <Flex alignItems="center" key={prompt.id}>
                                            <Box
                                                data-id={`prompt-library-prompt-${prompt.id}`}
                                                key={prompt.id}
                                                cursor="pointer"
                                                borderRadius="8px"
                                                border="1px solid rgb(216, 215, 213)"
                                                px="16px"
                                                py="8px"
                                                pr=""
                                                color={textColor}
                                                fontSize="14px"
                                                alignItems="center"
                                                display="flex"
                                                onClick={() => {
                                                    startPromptConversation(prompt)
                                                }}
                                            >
                                                {prompt.promptDescription}
                                            </Box>

                                            {prompt.isAssistant && <Image src={assistantIcon} padding="8px" paddingLeft="12px"></Image>}
                                        </Flex>
                                    ))}
                                </VStack>
                            </div>
                        ))}
                    </Box>
                </Box>
            </DrawerBody>
            <DrawerFooter borderTopWidth="1px" borderColor={borderColor} display="block" mx="24px" px="0" py="24px">
                <Button variant="secondary" mr="18px" onClick={onClose} textTransform="uppercase" letterSpacing="0.25px">
                    {t('translate.mobile.drawer.buttons.close')}
                </Button>
            </DrawerFooter>
        </DrawerDialog>
    )
}

export default PromptLibraryDrawer
